import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "text", "spinner", "confirmModal", "confirmTitle", "confirmMessage", "confirmButton", "confirmText", "confirmSpinner", "errorModal", "errorMessage"]
  static values = { ideaId: Number, action: String }

  connect() {
    // Modern Turbo Streams handle real-time updates, no polling needed
  }

    evaluate(event) {
    event.preventDefault()
    
    const action = this.actionValue || 'evaluate'
    const ideaId = this.ideaIdValue
    
    // Set up confirmation modal
    const confirmMessage = action === 'retry' ? 
      'Retry AI evaluation for this idea?' : 
      'Start AI evaluation for this idea?'
      
    const confirmText = action === 'retry' ? 'Retry Evaluation' : 'Start Evaluation'
    
    if (this.hasConfirmMessageTarget) {
      this.confirmMessageTarget.textContent = confirmMessage
    }
    if (this.hasConfirmTextTarget) {
      this.confirmTextTarget.textContent = confirmText
    }
    
    // Store current action for confirmation
    this.currentAction = action
    this.currentIdeaId = ideaId
    
    // Show confirmation modal
    if (this.hasConfirmModalTarget) {
      try {
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modal = new bootstrap.Modal(this.confirmModalTarget)
          modal.show()
        } else {
          // Fallback: show modal using native methods
          this.confirmModalTarget.classList.add('show')
          this.confirmModalTarget.style.display = 'block'
          document.body.classList.add('modal-open')
        }
      } catch (error) {
        // Simple fallback
        if (confirm(confirmMessage)) {
          this.confirmEvaluation({ preventDefault: () => {} })
        }
      }
    }
  }

  confirmEvaluation(event) {
    event.preventDefault()
    
    // Show loading state in modal button
    if (this.hasConfirmTextTarget) {
      this.confirmTextTarget.classList.add('d-none')
    }
    if (this.hasConfirmSpinnerTarget) {
      this.confirmSpinnerTarget.classList.remove('d-none')
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
    }
    
    // Show loading state in main button
    this.showLoading()
    
    // Make API request
    fetch(`/contributors/ideas/${this.currentIdeaId}/evaluate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({ retry: this.currentAction === 'retry' })
    })
    .then(response => {
      return response.json()
    })
    .then(data => {
      // Hide confirmation modal immediately
      this.hideConfirmModal()
      
      if (data.success) {
        // Turbo Streams will handle real-time updates automatically
        // Update button state to show evaluation is starting
        this.updateButtonState('evaluating')
      } else {
        throw new Error(data.message || 'Failed to start evaluation')
      }
    })
    .catch(error => {
      // Hide confirmation modal
      this.hideConfirmModal()
      
      // Reset button states
      this.resetConfirmButton()
      this.resetMainButton()
      
      // Show error modal
      this.showError(error.message || 'Failed to start evaluation. Please try again.')
    })
  }

  showLoading() {
    if (this.hasTextTarget) {
      this.textTarget.classList.add('d-none')
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('d-none')
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
    }
  }

  showSuccess() {
    if (this.hasButtonTarget) {
      this.buttonTarget.innerHTML = `
        <span class="text-success">
          ✓ Evaluation Started!
        </span>
      `
    }
  }

  showError(message) {
    // Set error message
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
    
    // Show error modal
    if (this.hasErrorModalTarget) {
      const modal = new bootstrap.Modal(this.errorModalTarget)
      modal.show()
    }
  }

  resetConfirmButton() {
    if (this.hasConfirmTextTarget) {
      this.confirmTextTarget.classList.remove('d-none')
    }
    if (this.hasConfirmSpinnerTarget) {
      this.confirmSpinnerTarget.classList.add('d-none')
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false
    }
  }

  resetMainButton() {
    if (this.hasTextTarget) {
      this.textTarget.classList.remove('d-none')
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('d-none')
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
    }
  }

  // Removed startStatusPolling - Turbo Streams handle real-time updates

  // Helper method to hide the confirmation modal
  hideConfirmModal() {
    if (this.hasConfirmModalTarget) {
      try {
        // Try to get existing instance
        const modal = bootstrap.Modal.getInstance(this.confirmModalTarget)
        if (modal) {
          modal.hide()
        } else {
          // Create new instance and hide
          const newModal = new bootstrap.Modal(this.confirmModalTarget)
          newModal.hide()
        }
      } catch (error) {
        // Fallback: hide modal manually
        this.confirmModalTarget.classList.remove('show')
        this.confirmModalTarget.style.display = 'none'
        document.body.classList.remove('modal-open')
        
        // Remove backdrop if it exists
        const backdrop = document.querySelector('.modal-backdrop')
        if (backdrop) {
          backdrop.remove()
        }
      }
    }
  }
} 